/**
 * MissionsAdminDialog — Developer Panel CMS for the mission catalog.
 *
 * Capabilities:
 *   - List every mission (incl. disabled), click to edit.
 *   - Create a new mission via the "+ Add Mission" header button.
 *   - Edit ALL fields covered by the backend MissionPayload model:
 *       display: id (immutable when editing), label, short, icon, color
 *       requirements: required_units (UnitRow list), alternates (OR-group list)
 *       timing/rewards: on_scene_duration, reward, score_gain, xp,
 *                       spawn_weight, assess_seconds
 *       patients: min, max, transport_pct, specialty {Trauma, Cath Lab, Stroke}
 *       upgrades: list of (target mission id + chance) rows
 *       enabled flag (also surfaced as a quick-toggle on the list row)
 *   - Delete a mission (with confirm prompt). Backend semantics: in-flight
 *     calls of the deleted type are NOT cancelled; this just stops spawning.
 *
 * All saves go through the dev-only `/api/dev/missions` routes. On every
 * mutation we re-fetch the catalog AND re-hydrate `EMERGENCY_TYPES` live so
 * the current player sees the change without a page reload.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ClipboardText,
  CaretLeft,
  Plus,
  Trash,
  FloppyDisk,
  CircleNotch,
  ToggleLeft,
  ToggleRight,
  PencilSimple,
  CopySimple,
} from "@phosphor-icons/react";
import {
  devListMissions,
  devCreateMission,
  devUpdateMission,
  devDeleteMission,
  devToggleMission,
  listMissions,
  formatApiError,
} from "@/lib/api";
import { hydrateEmergencyTypes, UNIT_TYPES } from "@/lib/gameConfig";
import { toast } from "sonner";

const SPECIALTIES = ["Trauma", "Cath Lab", "Stroke"];

// Live-hydrate EMERGENCY_TYPES after any mutation so the running game sees
// the edits without a full reload.
async function rehydrateLive() {
  try {
    const r = await listMissions();
    if (r?.missions) hydrateEmergencyTypes(r.missions);
  } catch (_e) {
    /* non-fatal */
  }
}

// Blank mission template for the "Add" form.
const BLANK_MISSION = {
  id: "",
  label: "",
  short: "",
  icon: "/icons/fire_icon.png",
  color: "#FF9F0A",
  required_units: {},
  alternates: [],
  on_scene_duration: 30,
  reward: 500,
  xp: 10,
  score_gain: 80,
  spawn_weight: 1.0,
  assess_seconds: null,
  patients: null,
  upgrades: [],
  enabled: true,
  service_type: "fire",       // fire | ems | police | multi
  arrest_chance_pct: 0,       // police-only: 0-100 chance an arrestee spawns on-scene
  event_only: false,          // gates mission from normal spawn pool — event-only
};

export default function MissionsAdminDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [missions, setMissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);  // null | "__new__" | <mission id>
  const [prefill, setPrefill] = useState(null);        // populated when duplicating
  const [search, setSearch] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await devListMissions();
      setMissions(r.missions || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load missions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) { setSelectedId(null); setSearch(""); setPrefill(null); }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return missions;
    return missions.filter(
      (m) =>
        m.id.toLowerCase().includes(q) ||
        (m.label || "").toLowerCase().includes(q) ||
        (m.short || "").toLowerCase().includes(q),
    );
  }, [missions, search]);

  // Reverse-upgrade index: `{targetMissionId: [sourceMissionId, ...]}`.
  // Lets each list row show which OTHER missions can escalate INTO it —
  // surfaces escalation chains and warns devs before deleting a mission
  // that other missions still reference as an upgrade target.
  const incomingUpgrades = useMemo(() => {
    const map = {};
    for (const m of missions) {
      for (const u of (m.upgrades || [])) {
        if (!u?.to) continue;
        (map[u.to] = map[u.to] || []).push({ from: m.id, chance: u.chance });
      }
    }
    return map;
  }, [missions]);

  const selected = selectedId === "__new__"
    ? (prefill || BLANK_MISSION)
    : missions.find((m) => m.id === selectedId);

  const handleToggle = async (mid, e) => {
    e?.stopPropagation();
    try {
      const r = await devToggleMission(mid);
      toast.success(`Mission ${r.enabled ? "enabled" : "disabled"}`);
      await refresh();
      await rehydrateLive();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Toggle failed");
    }
  };

  // Duplicate: open the editor in "new" mode pre-filled from `src`. Picks
  // the first available id suffix (`_copy`, then `_copy2`, `_copy3`…) so the
  // create POST doesn't collide with an existing id.
  const handleDuplicate = (src, e) => {
    e?.stopPropagation();
    const existingIds = new Set(missions.map((m) => m.id));
    let candidate = `${src.id}_copy`;
    let n = 2;
    while (existingIds.has(candidate)) {
      candidate = `${src.id}_copy${n}`;
      n += 1;
    }
    setPrefill({
      ...src,
      id: candidate,
      label: `${src.label} (COPY)`,
      // Deep-copy nested objects so editing the duplicate doesn't mutate
      // the source mission still rendered on the list behind it.
      required_units: { ...(src.required_units || {}) },
      alternates: (src.alternates || []).map((a) => ({ ...a })),
      upgrades: (src.upgrades || []).map((u) => ({ ...u })),
      patients: src.patients
        ? { ...src.patients, specialty: { ...(src.patients.specialty || {}) } }
        : null,
      enabled: src.enabled !== false,
    });
    setSelectedId("__new__");
  };

  const handleDelete = async (mid, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete mission "${mid}" permanently?\n\nIn-flight calls of this type will still resolve naturally — this only stops new spawns.`)) return;
    try {
      await devDeleteMission(mid);
      toast.success(`Deleted ${mid}`);
      if (selectedId === mid) setSelectedId(null);
      await refresh();
      await rehydrateLive();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="missions-admin-dialog"
        className="rounded-none border border-amber-500/30 bg-[#0A0E14] text-white max-w-4xl p-0 max-h-[90vh] grid grid-rows-[auto_minmax(0,1fr)] gap-0"
      >
        <DialogHeader className="px-5 py-3 border-b border-amber-500/20 bg-amber-500/[0.04]">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-amber-300 flex items-center gap-2">
            <ClipboardText size={14} weight="fill" /> Missions
            <span className="text-zinc-500 ml-2 text-[10px] tracking-wider font-normal">
              {missions.length} total · {missions.filter((m) => m.enabled).length} enabled
            </span>
            <span className="flex-1" />
            {!selectedId && (
              <Button
                data-testid="mission-add-btn"
                size="sm"
                onClick={() => { setPrefill(null); setSelectedId("__new__"); }}
                className="rounded-none h-7 px-2 mono uppercase text-[10px] tracking-widest bg-emerald-600 hover:bg-emerald-500 text-black mr-6"
              >
                <Plus size={11} className="mr-1.5" /> Add Mission
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto px-5 py-3">
          {selectedId ? (
            <MissionEditor
              key={selectedId}
              mission={selected}
              isNew={selectedId === "__new__"}
              onBack={() => setSelectedId(null)}
              onSaved={async () => {
                await refresh();
                await rehydrateLive();
                setSelectedId(null);
                setPrefill(null);
              }}
              missions={missions}
            />
          ) : (
            <MissionList
              missions={filtered}
              search={search}
              setSearch={setSearch}
              loading={loading}
              onSelect={setSelectedId}
              onToggle={handleToggle}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              incomingUpgrades={incomingUpgrades}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- List view ----------

function MissionList({ missions, search, setSearch, loading, onSelect, onToggle, onDuplicate, onDelete, incomingUpgrades }) {
  return (
    <>
      <div className="flex items-center gap-2 mb-3 sticky top-0 bg-[#0A0E14] py-1 z-10">
        <Input
          data-testid="missions-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by id / label / short code…"
          className="rounded-none h-8 bg-black/40 border-amber-500/20 text-[12px] mono"
        />
      </div>
      {loading && missions.length === 0 ? (
        <div className="text-center py-10 text-zinc-500 mono text-[11px]">Loading…</div>
      ) : missions.length === 0 ? (
        <div className="text-center py-10 text-zinc-500 mono text-[11px]">No missions match</div>
      ) : (
        <div className="space-y-1">
          {missions.map((m) => {
            const inc = (incomingUpgrades || {})[m.id] || [];
            return (
            <div
              key={m.id}
              data-testid={`mission-row-${m.id}`}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(m.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(m.id); } }}
              className="grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-2 px-2 py-1.5 border-l-2 border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 cursor-pointer transition-colors"
            >
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: m.color || "#FF9F0A" }} />
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 truncate">
                  <span className="mono text-[11px] uppercase tracking-widest text-white truncate">{m.label}</span>
                  <span className="mono text-[9px] text-zinc-500 truncate">{m.id}</span>
                  {!m.enabled && (
                    <span className="mono text-[8px] uppercase tracking-widest px-1 border border-red-500/40 text-red-300 shrink-0">disabled</span>
                  )}
                  {inc.length > 0 && (
                    <span
                      data-testid={`mission-incoming-${m.id}`}
                      title={`Upgraded into from: ${inc.map((u) => `${u.from} (${Math.round((u.chance || 0) * 100)}%)`).join(", ")}`}
                      className="mono text-[8px] uppercase tracking-widest px-1 border border-cyan-400/40 text-cyan-300 bg-cyan-500/5 shrink-0"
                    >
                      ↑ {inc.length}
                    </span>
                  )}
                </div>
                <div className="mono text-[9px] text-zinc-400 truncate">
                  {summarizeRequirements(m)} · ${Number(m.reward || 0).toLocaleString()} · {m.on_scene_duration}s
                </div>
                {inc.length > 0 && (
                  <div className="mono text-[9px] text-cyan-400/70 truncate" data-testid={`mission-incoming-list-${m.id}`}>
                    ← {inc.map((u) => u.from).slice(0, 3).join(", ")}{inc.length > 3 ? `, +${inc.length - 3} more` : ""}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  data-testid={`mission-toggle-${m.id}`}
                  onClick={(e) => onToggle(m.id, e)}
                  className="w-7 h-7 inline-flex items-center justify-center hover:bg-white/10 text-zinc-300"
                  title={m.enabled ? "Disable mission" : "Enable mission"}
                >
                  {m.enabled ? <ToggleRight size={16} className="text-emerald-400" /> : <ToggleLeft size={16} className="text-zinc-500" />}
                </button>
                <button
                  type="button"
                  data-testid={`mission-edit-${m.id}`}
                  onClick={(e) => { e.stopPropagation(); onSelect(m.id); }}
                  className="w-7 h-7 inline-flex items-center justify-center hover:bg-white/10 text-zinc-300"
                  title="Edit mission"
                >
                  <PencilSimple size={13} />
                </button>
                <button
                  type="button"
                  data-testid={`mission-duplicate-${m.id}`}
                  onClick={(e) => onDuplicate(m, e)}
                  className="w-7 h-7 inline-flex items-center justify-center hover:bg-cyan-500/15 text-cyan-300"
                  title="Duplicate mission"
                >
                  <CopySimple size={13} />
                </button>
                <button
                  type="button"
                  data-testid={`mission-delete-${m.id}`}
                  onClick={(e) => onDelete(m.id, e)}
                  className="w-7 h-7 inline-flex items-center justify-center hover:bg-red-500/15 text-red-400"
                  title="Delete mission"
                >
                  <Trash size={13} />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function summarizeRequirements(m) {
  const fmt = (mapObj) =>
    Object.entries(mapObj || {})
      .filter(([, n]) => n > 0)
      .map(([k, n]) => `${n}× ${k}`)
      .join(" + ");
  const main = fmt(m.required_units) || "(none)";
  if (Array.isArray(m.alternates) && m.alternates.length) {
    return `${main}  |  ${m.alternates.map(fmt).join(" | ")}`;
  }
  return main;
}

// ---------- Editor ----------

function MissionEditor({ mission, isNew, onBack, onSaved, missions }) {
  // Local form state — initialized from the incoming mission (or BLANK).
  const [form, setForm] = useState(() => ({
    ...mission,
    required_units: { ...(mission.required_units || {}) },
    alternates: (mission.alternates || []).map((a) => ({ ...a })),
    upgrades: (mission.upgrades || []).map((u) => ({ ...u })),
    patients: mission.patients ? { ...mission.patients, specialty: { ...(mission.patients.specialty || {}) }, hems_required_pct: mission.patients.hems_required_pct ?? 0 } : null,
  }));
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build payload — server-side validator accepts the same shape.
      const payload = {
        id: form.id.trim(),
        label: (form.label || "").trim(),
        short: (form.short || "").trim(),
        icon: (form.icon || "").trim() || "/icons/fire_icon.png",
        color: (form.color || "").trim() || "#FF9F0A",
        required_units: cleanUnitMap(form.required_units),
        alternates: (form.alternates || []).map(cleanUnitMap).filter((o) => Object.keys(o).length > 0),
        on_scene_duration: Number(form.on_scene_duration) || 0,
        reward: Number(form.reward) || 0,
        xp: Number(form.xp) || 0,
        score_gain: Number(form.score_gain) || 0,
        spawn_weight: form.spawn_weight === "" || form.spawn_weight == null ? 1.0 : Number(form.spawn_weight),
        assess_seconds: form.assess_seconds === "" || form.assess_seconds == null ? null : Number(form.assess_seconds),
        patients: form.patients
          ? {
              min: Number(form.patients.min) || 0,
              max: Number(form.patients.max) || 0,
              transport_pct: Number(form.patients.transport_pct) || 0,
              specialty: {
                Trauma: Number(form.patients.specialty?.Trauma) || 0,
                "Cath Lab": Number(form.patients.specialty?.["Cath Lab"]) || 0,
                Stroke: Number(form.patients.specialty?.Stroke) || 0,
              },
              hems_required_pct: Number(form.patients.hems_required_pct) || 0,
            }
          : null,
        upgrades: (form.upgrades || [])
          .filter((u) => u.to && Number(u.chance) > 0)
          .map((u) => ({ to: u.to, chance: Math.max(0, Math.min(1, Number(u.chance))) })),
        enabled: form.enabled !== false,
        service_type: form.service_type || "fire",
        arrest_chance_pct: Number(form.arrest_chance_pct) || 0,
        event_only: !!form.event_only,
      };
      if (isNew) {
        if (!payload.id) { toast.error("Mission ID is required"); setSaving(false); return; }
        if (!/^[a-z0-9_]+$/.test(payload.id)) { toast.error("ID must be lowercase letters, numbers, underscores"); setSaving(false); return; }
        await devCreateMission(payload);
        toast.success("Mission created");
      } else {
        // PATCH — drop `id` (immutable on the server).
        const { id: _id, ...patch } = payload;
        await devUpdateMission(mission.id, patch);
        toast.success("Mission saved");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // List of mission ids you can pick as an upgrade target (excludes self).
  const otherMissionIds = missions.map((m) => m.id).filter((id) => id !== form.id);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 sticky top-0 bg-[#0A0E14] py-1 z-10">
        <Button
          data-testid="mission-back-btn"
          variant="outline"
          size="sm"
          onClick={onBack}
          className="rounded-none h-7 px-2 mono uppercase text-[10px] tracking-widest border-white/15 text-zinc-300"
        >
          <CaretLeft size={11} className="mr-1" /> Back
        </Button>
        <h2 className="mono uppercase tracking-widest text-[11px] text-amber-300">
          {isNew ? "Create New Mission" : `Edit · ${mission.id}`}
        </h2>
        <span className="flex-1" />
        <Button
          data-testid="mission-save-btn"
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="rounded-none h-7 px-3 mono uppercase text-[10px] tracking-widest bg-emerald-600 hover:bg-emerald-500 text-black"
        >
          {saving ? <CircleNotch size={11} className="animate-spin mr-1.5" /> : <FloppyDisk size={11} className="mr-1.5" />}
          Save
        </Button>
      </div>

      {/* Display section */}
      <Section title="Display">
        <Grid cols={2}>
          <Field label="ID (immutable after create)" testid="field-id">
            <Input
              data-testid="mission-id-input"
              value={form.id}
              disabled={!isNew}
              onChange={(e) => set({ id: e.target.value })}
              placeholder="e.g. house_fire"
              className="rounded-none h-8 bg-black/40 border-amber-500/20 text-[12px] mono"
            />
          </Field>
          <Field label="Label" testid="field-label">
            <Input
              data-testid="mission-label-input"
              value={form.label}
              onChange={(e) => set({ label: e.target.value })}
              placeholder="HOUSE FIRE"
              className="rounded-none h-8 bg-black/40 border-amber-500/20 text-[12px] mono"
            />
          </Field>
          <Field label="Short Code" testid="field-short">
            <Input
              data-testid="mission-short-input"
              value={form.short}
              onChange={(e) => set({ short: e.target.value })}
              placeholder="HOUSE"
              className="rounded-none h-8 bg-black/40 border-amber-500/20 text-[12px] mono"
            />
          </Field>
          <Field label="Color (hex)" testid="field-color">
            <div className="flex items-center gap-2">
              <Input
                data-testid="mission-color-input"
                value={form.color}
                onChange={(e) => set({ color: e.target.value })}
                placeholder="#FF3B30"
                className="rounded-none h-8 bg-black/40 border-amber-500/20 text-[12px] mono"
              />
              <span className="inline-block w-7 h-7 border border-white/15" style={{ background: form.color || "#FF9F0A" }} />
            </div>
          </Field>
          <Field label="Icon path or URL" testid="field-icon" colSpan={2}>
            <Input
              data-testid="mission-icon-input"
              value={form.icon}
              onChange={(e) => set({ icon: e.target.value })}
              placeholder="/icons/fire_icon.png"
              className="rounded-none h-8 bg-black/40 border-amber-500/20 text-[12px] mono"
            />
          </Field>
          <Field label="Enabled" testid="field-enabled" colSpan={2}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                data-testid="mission-enabled-input"
                type="checkbox"
                checked={form.enabled !== false}
                onChange={(e) => set({ enabled: e.target.checked })}
                className="accent-emerald-500"
              />
              <span className="mono text-[11px] text-zinc-300">
                {form.enabled !== false ? "Mission is ENABLED (will spawn)" : "Mission is DISABLED (no spawn)"}
              </span>
            </label>
          </Field>
        </Grid>
      </Section>

      {/* Requirements */}
      <Section title="Required Units (primary)">
        <UnitMapEditor value={form.required_units} onChange={(v) => set({ required_units: v })} testidPrefix="req" />
      </Section>

      <Section title={`Alternate Requirement Groups (OR-options) — ${form.alternates.length}`}>
        <p className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2">
          Each group is a separate way to satisfy the call. The first group fully met on scene resolves the call.
        </p>
        {form.alternates.map((alt, idx) => (
          <div key={idx} className="mb-3 border-l-2 border-amber-500/30 pl-2" data-testid={`alt-group-${idx}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="mono text-[10px] uppercase tracking-widest text-amber-300">Option {idx + 1}</span>
              <button
                type="button"
                data-testid={`alt-remove-${idx}`}
                onClick={() => set({ alternates: form.alternates.filter((_, i) => i !== idx) })}
                className="text-[10px] mono uppercase tracking-widest text-red-300 hover:text-red-200 px-1"
              >
                Remove
              </button>
            </div>
            <UnitMapEditor
              value={alt}
              onChange={(v) => set({ alternates: form.alternates.map((a, i) => (i === idx ? v : a)) })}
              testidPrefix={`alt-${idx}`}
            />
          </div>
        ))}
        <Button
          data-testid="alt-add-btn"
          size="sm"
          variant="outline"
          onClick={() => set({ alternates: [...form.alternates, {}] })}
          className="rounded-none h-7 px-2 mono uppercase text-[10px] tracking-widest border-amber-500/30 text-amber-300"
        >
          <Plus size={11} className="mr-1" /> Add OR-option
        </Button>
      </Section>

      {/* Timing & rewards */}
      <Section title="Timing & Rewards">
        <Grid cols={3}>
          <Field label="On-scene seconds" testid="field-osd">
            <NumInput data-testid="mission-osd-input" value={form.on_scene_duration} onChange={(v) => set({ on_scene_duration: v })} />
          </Field>
          <Field label="Reward ($)" testid="field-reward">
            <NumInput data-testid="mission-reward-input" value={form.reward} onChange={(v) => set({ reward: v })} />
          </Field>
          <Field label="XP" testid="field-xp">
            <NumInput data-testid="mission-xp-input" value={form.xp} onChange={(v) => set({ xp: v })} />
          </Field>
          <Field label="Score Gain" testid="field-score">
            <NumInput data-testid="mission-score-input" value={form.score_gain} onChange={(v) => set({ score_gain: v })} />
          </Field>
          <Field label="Spawn weight (0 = never)" testid="field-weight">
            <NumInput data-testid="mission-weight-input" value={form.spawn_weight} onChange={(v) => set({ spawn_weight: v })} step="0.1" />
          </Field>
          <Field label="Assess seconds (blank=default 30)" testid="field-assess">
            <NumInput
              data-testid="mission-assess-input"
              value={form.assess_seconds ?? ""}
              onChange={(v) => set({ assess_seconds: v === "" ? null : v })}
              allowEmpty
            />
          </Field>
          <Field label="Service Type" testid="field-service-type">
            <select
              data-testid="mission-service-type"
              value={form.service_type || "fire"}
              onChange={(e) => set({ service_type: e.target.value })}
              className="w-full bg-[#0F1115] border border-white/10 rounded-none px-2 py-1 text-[12px] mono uppercase tracking-widest text-zinc-100"
            >
              <option value="fire">Fire</option>
              <option value="ems">EMS</option>
              <option value="police">Police</option>
              <option value="multi">Multi</option>
            </select>
          </Field>
          <Field label="Arrest chance % (police only)" testid="field-arrest-pct">
            <NumInput
              data-testid="mission-arrest-pct"
              value={form.arrest_chance_pct ?? 0}
              onChange={(v) => set({ arrest_chance_pct: v })}
            />
          </Field>
          <Field label="Event Only" testid="field-event-only">
            <label className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-zinc-300">
              <input
                type="checkbox"
                data-testid="mission-event-only"
                checked={!!form.event_only}
                onChange={(e) => set({ event_only: e.target.checked })}
              />
              Only spawn during an active Event
            </label>
          </Field>
        </Grid>
      </Section>

      {/* Patients */}
      <Section title="Patients">
        <label className="flex items-center gap-2 cursor-pointer mb-3">
          <input
            data-testid="mission-patients-enabled"
            type="checkbox"
            checked={!!form.patients}
            onChange={(e) =>
              set({
                patients: e.target.checked
                  ? form.patients || { min: 1, max: 1, transport_pct: 50, specialty: { Trauma: 0, "Cath Lab": 0, Stroke: 0 }, hems_required_pct: 0 }
                  : null,
              })
            }
            className="accent-emerald-500"
          />
          <span className="mono text-[11px] text-zinc-300">This mission can generate patients</span>
        </label>
        {form.patients && (
          <Grid cols={3}>
            <Field label="Min patients" testid="field-pmin">
              <NumInput data-testid="patient-min-input" value={form.patients.min} onChange={(v) => set({ patients: { ...form.patients, min: v } })} />
            </Field>
            <Field label="Max patients" testid="field-pmax">
              <NumInput data-testid="patient-max-input" value={form.patients.max} onChange={(v) => set({ patients: { ...form.patients, max: v } })} />
            </Field>
            <Field label="Transport %" testid="field-trans">
              <NumInput data-testid="patient-transport-input" value={form.patients.transport_pct} onChange={(v) => set({ patients: { ...form.patients, transport_pct: v } })} max={100} />
            </Field>
            {SPECIALTIES.map((sp) => (
              <Field key={sp} label={`${sp} % (when transported)`} testid={`field-spec-${sp.replace(/\s+/g, "")}`}>
                <NumInput
                  data-testid={`patient-spec-${sp.replace(/\s+/g, "").toLowerCase()}-input`}
                  value={form.patients.specialty?.[sp] || 0}
                  onChange={(v) =>
                    set({
                      patients: {
                        ...form.patients,
                        specialty: { ...(form.patients.specialty || {}), [sp]: v },
                      },
                    })
                  }
                  max={100}
                />
              </Field>
            ))}
            <Field label="HEMS Required % (critical, no ground)" testid="field-hems-req">
              <NumInput
                data-testid="patient-hems-required-input"
                value={form.patients.hems_required_pct || 0}
                onChange={(v) => set({ patients: { ...form.patients, hems_required_pct: v } })}
                max={100}
              />
            </Field>
          </Grid>
        )}
      </Section>

      {/* Upgrades */}
      <Section title={`Upgrades (chain to other missions) — ${form.upgrades.length}`}>
        <p className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2">
          When this call spawns, each entry rolls independently. The first roll that passes upgrades the call to the target mission id.
        </p>
        {form.upgrades.map((u, idx) => (
          <div key={idx} className="grid grid-cols-[minmax(0,1fr)_120px_36px] gap-2 mb-2 items-center" data-testid={`upgrade-row-${idx}`}>
            <Select
              value={u.to || ""}
              onValueChange={(v) => set({ upgrades: form.upgrades.map((x, i) => (i === idx ? { ...x, to: v } : x)) })}
            >
              <SelectTrigger data-testid={`upgrade-target-${idx}`} className="rounded-none h-8 bg-black/40 border-amber-500/20 text-[11px] mono">
                <SelectValue placeholder="Select target mission" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0E14] border-amber-500/20 text-white">
                {otherMissionIds.map((mid) => (
                  <SelectItem key={mid} value={mid} className="mono text-[11px]">{mid}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <NumInput
              data-testid={`upgrade-chance-${idx}`}
              value={u.chance}
              onChange={(v) => set({ upgrades: form.upgrades.map((x, i) => (i === idx ? { ...x, chance: v } : x)) })}
              step="0.01"
              max={1}
              placeholder="0.0 - 1.0"
            />
            <button
              type="button"
              data-testid={`upgrade-remove-${idx}`}
              onClick={() => set({ upgrades: form.upgrades.filter((_, i) => i !== idx) })}
              className="w-8 h-8 inline-flex items-center justify-center hover:bg-red-500/15 text-red-400"
              title="Remove upgrade"
            >
              <Trash size={13} />
            </button>
          </div>
        ))}
        <Button
          data-testid="upgrade-add-btn"
          size="sm"
          variant="outline"
          onClick={() => set({ upgrades: [...form.upgrades, { to: "", chance: 0.1 }] })}
          className="rounded-none h-7 px-2 mono uppercase text-[10px] tracking-widest border-amber-500/30 text-amber-300"
        >
          <Plus size={11} className="mr-1" /> Add upgrade
        </Button>
      </Section>
    </div>
  );
}

// ---------- Small editor primitives ----------

function Section({ title, children }) {
  return (
    <div className="border border-amber-500/15 bg-amber-500/[0.03] p-3">
      <div className="mono uppercase tracking-widest text-[10px] text-amber-300 mb-2">{title}</div>
      {children}
    </div>
  );
}

function Grid({ cols = 2, children }) {
  const cls = cols === 3 ? "grid-cols-3" : "grid-cols-2";
  return <div className={`grid ${cls} gap-3`}>{children}</div>;
}

function Field({ label, children, testid, colSpan }) {
  const span = colSpan === 2 ? "col-span-2" : colSpan === 3 ? "col-span-3" : "";
  return (
    <div className={span} data-testid={testid}>
      <div className="mono uppercase tracking-widest text-[9px] text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, step = "1", max, allowEmpty, placeholder, ...rest }) {
  return (
    <Input
      type="number"
      value={value === null || value === undefined ? "" : value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "" && allowEmpty) onChange("");
        else onChange(v === "" ? 0 : Number(v));
      }}
      step={step}
      max={max}
      placeholder={placeholder}
      className="rounded-none h-8 bg-black/40 border-amber-500/20 text-[12px] mono"
      {...rest}
    />
  );
}

function UnitMapEditor({ value, onChange, testidPrefix }) {
  // value is a {UnitType: count} object. Render one row per non-zero entry +
  // one "add" dropdown for missing types.
  const entries = Object.entries(value || {}).filter(([, n]) => n != null);
  const used = new Set(entries.map(([k]) => k));
  const available = UNIT_TYPES.filter((u) => !used.has(u));
  const set = (k, n) => {
    const next = { ...value };
    if (n <= 0) delete next[k];
    else next[k] = n;
    onChange(next);
  };
  return (
    <div className="space-y-1">
      {entries.length === 0 && (
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">No units required</div>
      )}
      {entries.map(([unit, count]) => (
        <div key={unit} className="grid grid-cols-[minmax(0,1fr)_80px_36px] gap-2 items-center" data-testid={`${testidPrefix}-row-${unit.replace(/\s+/g, "")}`}>
          <span className="mono text-[11px] text-zinc-200 px-2 py-1 bg-white/5 border border-white/10 truncate">{unit}</span>
          <NumInput data-testid={`${testidPrefix}-count-${unit.replace(/\s+/g, "")}`} value={count} onChange={(v) => set(unit, Number(v) || 0)} />
          <button
            type="button"
            data-testid={`${testidPrefix}-remove-${unit.replace(/\s+/g, "")}`}
            onClick={() => set(unit, 0)}
            className="w-8 h-8 inline-flex items-center justify-center hover:bg-red-500/15 text-red-400"
            title="Remove"
          >
            <Trash size={13} />
          </button>
        </div>
      ))}
      {available.length > 0 && (
        <div className="grid grid-cols-[minmax(0,1fr)_80px_36px] gap-2 items-center pt-1" data-testid={`${testidPrefix}-add-row`}>
          <Select value="" onValueChange={(unit) => set(unit, 1)}>
            <SelectTrigger data-testid={`${testidPrefix}-add-select`} className="rounded-none h-8 bg-black/40 border-amber-500/20 text-[11px] mono">
              <SelectValue placeholder="+ Add unit type…" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0E14] border-amber-500/20 text-white">
              {available.map((u) => (
                <SelectItem key={u} value={u} className="mono text-[11px]">{u}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div />
          <div />
        </div>
      )}
    </div>
  );
}

function cleanUnitMap(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) out[k] = Math.floor(n);
  }
  return out;
}
